import { useEffect, useRef, useState } from "react";
import { Globe, Check, ChevronDown, Sparkles } from "lucide-react";
import { APP_LANGS, getLang, setLang, initTranslator } from "@/lib/translator";

export const LANGUAGES = APP_LANGS;

/**
 * Boots the LLM page translator once per page load.
 * (Replaces the old Google Translate widget initialiser.)
 */
export function TranslatorInit() {
    useEffect(() => { initTranslator(); }, []);
    return null;
}

/** Custom-styled language picker — 8 languages, AI-translated. */
export function LanguageSwitcher({ className = "" }) {
    const [open, setOpen] = useState(false);
    const [current] = useState(() => getLang());
    const ref = useRef(null);

    useEffect(() => {
        function close(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }
        document.addEventListener("mousedown", close);
        return () => document.removeEventListener("mousedown", close);
    }, []);

    const pick = (code) => {
        if (code === current) { setOpen(false); return; }
        setLang(code); // persists + reloads so the translator re-runs cleanly
    };

    const cur = LANGUAGES.find((l) => l.code === current) || LANGUAGES[0];
    return (
        <div ref={ref} className={`relative notranslate ${className}`}>
            <button
                onClick={() => setOpen((v) => !v)}
                data-testid="lang-switcher"
                aria-label="Choose language"
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-slate-200 hover:border-slate-400 text-[12px] font-mono-tr tracking-[0.12em] uppercase text-slate-700 transition notranslate"
            >
                <Globe size={13} />
                <span>{cur.code.toUpperCase()}</span>
                <ChevronDown size={12} className={`transition-transform ${open ? "rotate-180" : ""}`} />
            </button>
            {open && (
                <div className="absolute top-full mt-2 right-0 w-56 bg-white rounded-xl border border-slate-200 shadow-xl z-50 py-1.5 notranslate" data-testid="lang-menu">
                    {LANGUAGES.map((l) => {
                        const active = l.code === current;
                        return (
                            <button
                                key={l.code}
                                onClick={() => pick(l.code)}
                                data-testid={`lang-opt-${l.code}`}
                                className={`w-full flex items-center justify-between gap-3 px-3.5 py-2 text-[13.5px] text-left hover:bg-slate-50 transition-colors ${active ? "bg-slate-50" : ""}`}
                            >
                                <span>
                                    <span className="text-slate-900 font-medium">{l.native}</span>
                                    <span className="text-slate-400 text-[11.5px] ms-1.5">{l.label}</span>
                                </span>
                                {active && <Check size={14} className="text-cyan-600" />}
                            </button>
                        );
                    })}
                    <div className="px-3.5 pt-1.5 mt-1 text-[10.5px] text-slate-400 border-t border-slate-100 leading-relaxed flex items-center gap-1">
                        <Sparkles size={10} className="text-cyan-500" />
                        AI-translated · professional quality
                    </div>
                </div>
            )}
        </div>
    );
}
